import { FeatureCollection } from '@turf/helpers'
import dayjs from 'dayjs'
import upperFirst from 'lodash/upperFirst'
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import api from 'src/api'
import {
  GraphStackResponse,
  GraphBucketWithTotalResponse,
  GraphCompareWithTotalResponse,
  GraphFlowsResponse,
  GraphFilters_ZoneEnum,
  GraphFilters,
  GraphData,
  FeatureCollection_TypeEnum,
  GraphStackWithAverageResponse,
  GraphCompareResponse,
  DomainPermission_VerbEnum,
  DomainPermission_DomainEnum,
} from 'src/api/gen'
import { JsonListToCsv, downloadCsv } from 'src/commons/export'
import { formatExportComparing, formatExportTotal } from 'src/commons/export/Export'
import BaseChartWrapper from 'src/commons/graphs/BaseChartWrapper'
import { ChartWrapper, DashboardGraphWrapper } from 'src/commons/graphs/GraphWrapper/GraphWrappers'
import useDebouncedCallback from 'src/commons/hooks/useDebouncedCallback'
import { getTranslation } from 'src/commons/i18n'
import { enumsTranslationKeys } from 'src/commons/i18nEnums'
import { Can, userHasPermission } from 'src/commons/permissions'
import { IFeatureCollection, IGraphComponent, IFilters, ILink, INode, GranularityEnum } from 'src/commons/types'
import { createPayloadFromFilters } from 'src/commons/utils'
import WarnBanner from 'src/components/WarnBanner'
import { useAgencySettings } from 'src/layout/AgencySettings'
import { useProviderUser } from 'src/layout/hooks'
import { getLayerFeatures, useMapLayer } from 'src/layout/MapLayerWrapper'

import { graphDataToFeatureCollection } from '../DeploymentCharts'
import { scrollToComponent } from '../hooks'

import {
  NumberOfTripsPerDay,
  NumberOfTripsPerDayWithPreviousYear,
  NumberOfTripsPerProviderPerDay,
  TripsDuration,
  PercentageofTripsPerProvider,
  PercentageofTripsPerProviderWithPreviousYear,
  TotalDistancePerProvider,
  TotalDistance,
  TotalDistanceWithPreviousYear,
  TripsDistribution,
  TripsDistributionComparing,
  TripsCloseToPublicTransports,
} from './charts'
import {
  exportTripsDistribution,
  exportTripsDistributionComparing,
  exportTripsDuration,
  formatExportClosePublicTransportStops,
  formatExportTripsPerProvider,
  formatExportTripsPerZoneDestination,
  formatExportTripsPerZoneOrigin,
} from './exports'
import OriginDestinationSankey, { computeOriginDestination } from './OriginDestinationSankey'
import TripsPerZone from './TripsPerZone'

function exportOriginDestinationSankey(filters: IFilters, showInnerTraffic: boolean) {
  const formatData =
    () =>
    ({ links, nodes }: { links: ILink[]; nodes: INode[] }) => {
      const { t } = getTranslation()
      const originNodes = nodes.filter((n) => n.key.startsWith('origin'))
      const properties: {
        [key: string]: string | number
      }[] = originNodes.map((node) => ({
        [t('kpi.export.OriginDestination')]: node.name,
      }))
      links.forEach((link: ILink) => {
        const sourceNode = nodes[link.source]
        const targetNode = nodes[link.target]
        const propId = originNodes.map((n) => n.key).indexOf(sourceNode.key)
        properties[propId] = {
          ...properties[propId],
          [targetNode.name]: link.value,
        }
      })
      return properties
    }

  async function download(filename: string): Promise<any> {
    const { t } = getTranslation()
    // Re-fetch data with a very high limit to have all areas, not only the displayed ones
    const exportFilters = { ...filters, limit: undefined }
    const data = await api.graphs.graphTripFlows({ data: createPayloadFromFilters(exportFilters) })
    const results = computeOriginDestination(data, showInnerTraffic)
    try {
      const jsonList = formatData()(results)
      // Provide fixed headers to have a clean export with nodes sorted
      // by decreasing inner flow, both vertically and horizontally:
      //
      //    Origin\Destination         DOWNTOWN LOS ANGELES    VENICE NC
      //    DOWNTOWN LOS ANGELES       1893                    0
      //    VENICE NC                  0                       1142
      const headers = [
        t('kpi.export.OriginDestination'),
        ...jsonList.map((p) => `${p[t('kpi.export.OriginDestination')]}`),
      ]
      const csvContent = JsonListToCsv(jsonList, headers)
      downloadCsv(csvContent, filename)
    } catch (err) {
      console.error(err)
    }
  }
  return download
}

// Max number of areas for alluvial diagram
// We keep the ones with highest in-area traffic
const MAX_DISPLAYED_AREAS = 20

function useDebouncedLoadDataCallback<DataType>(
  api_query: (params: GraphFilters) => Promise<DataType>,
  filters: IFilters,
  setLoading: (value: boolean) => void,
  setData: (value: DataType) => void,
  previousYear = false
) {
  return useDebouncedCallback(async function () {
    try {
      setLoading(true)
      const payload = createPayloadFromFilters(filters)
      if (previousYear) {
        payload.time_range_from = dayjs(payload.time_range_from).subtract(1, 'years').toISOString()
        payload.time_range_to = dayjs(payload.time_range_to).subtract(1, 'years').toISOString()
      }
      setData(await api_query(payload))
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  })
}

export function TripsPerDayComponent(props: IGraphComponent) {
  const { t } = useTranslation()
  const [comparing, setComparing] = useState<boolean>(false)
  const [previousYear, setPreviousYear] = useState<boolean>(false)
  const [loading, setLoading] = useState(true)
  const [compareData, setCompareData] = useState<GraphCompareWithTotalResponse | undefined>(undefined)
  const [compareDataPreviousYear, setCompareDataPreviousYear] = useState<GraphCompareWithTotalResponse | undefined>(
    undefined
  )
  const [histogramData, setHistogramData] = useState<GraphBucketWithTotalResponse | undefined>(undefined)
  const [histogramDataPreviousYear, setHistogramDataPreviousYear] = useState<GraphBucketWithTotalResponse | undefined>(
    undefined
  )
  const isProviderUser = useProviderUser()

  const title = comparing ? upperFirst(t('kpi.TripsPerDay.title.comparing')) : upperFirst(t('kpi.TripsPerDay.title'))
  let description = comparing
    ? upperFirst(t('kpi.TripsPerDay.description.comparing'))
    : upperFirst(t('kpi.TripsPerDay.description'))
  if (previousYear) {
    const extraDescription = comparing
      ? t('kpi.TripsPerDay.previousYeardescription.comparing')
      : t('kpi.TripsPerDay.previousYeardescription')
    description = `${description}. ${extraDescription}`
  }

  const toggles = []
  if (userHasPermission(['read', 'trip']) && !isProviderUser) {
    toggles.push({
      active: comparing,
      onToggle: () => setComparing(!comparing),
      activeIcon: 'bar',
      inactiveIcon: 'line',
      activeText: t('kpi.TripsCharts.toggleIconActiveText'),
      inactiveText: t('kpi.TripsCharts.toggleIconInactiveText'),
    })
  }
  if ([GranularityEnum.quarter, GranularityEnum.month].includes(props.filters.granularity)) {
    toggles.push({
      active: previousYear,
      onToggle: () => setPreviousYear(!previousYear),
      activeIcon: 'review-off',
      inactiveIcon: 'review-on',
      activeText: t('kpi.TripsCharts.togglePreviousYearActiveText'),
      inactiveText: t('kpi.TripsCharts.togglePreviousYearInactiveText'),
    })
  }

  const fetchCompareData = useDebouncedLoadDataCallback(
    (payload: GraphFilters) => api.graphs.graphNumberOfTripsPerProvider({ data: payload }),
    props.filters,
    setLoading,
    setCompareData
  )
  const fetchCompareDataPreviousYear = useDebouncedLoadDataCallback(
    (payload: GraphFilters) => api.graphs.graphNumberOfTripsPerProvider({ data: payload }),
    props.filters,
    setLoading,
    setCompareDataPreviousYear,
    true
  )
  const fetchHistogramData = useDebouncedLoadDataCallback(
    (payload: GraphFilters) => api.graphs.graphNumberOfTrips({ data: payload }),
    props.filters,
    setLoading,
    setHistogramData
  )
  const fetchHistogramDataPreviousYear = useDebouncedLoadDataCallback(
    (payload: GraphFilters) => api.graphs.graphNumberOfTrips({ data: payload }),
    props.filters,
    setLoading,
    setHistogramDataPreviousYear,
    true
  )

  useEffect(() => {
    // When toggling comparing/previousYear, only fetch data if needed
    if (comparing) {
      fetchCompareData()
      if (previousYear) {
        fetchCompareDataPreviousYear()
      }
    } else {
      fetchHistogramData()
      if (previousYear) {
        fetchHistogramDataPreviousYear()
      }
    }
  }, [comparing, previousYear, props.filters])

  let formatExportBuckets
  if (userHasPermission(['analyze', 'kpi'])) {
    formatExportBuckets = comparing
      ? formatExportComparing(props.filters.granularity)
      : formatExportTotal(props.filters.granularity, t('kpi.export.TotalTrips'))
  }

  let chart: React.ReactNode
  let exportData: any
  if (!comparing && !previousYear) {
    exportData = histogramData
    chart = histogramData && (
      <NumberOfTripsPerDay
        title={title}
        graphReport={histogramData as GraphBucketWithTotalResponse}
        granularity={props.filters.granularity}
      />
    )
  } else if (!comparing && previousYear) {
    if (histogramData && histogramDataPreviousYear) {
      exportData = {
        data: histogramDataPreviousYear.data.map((element, index) => [element, histogramData.data[index]]).flat(),
      }
    }
    chart = histogramData && histogramDataPreviousYear && (
      <NumberOfTripsPerDayWithPreviousYear
        title={title}
        graphReport={histogramData as GraphBucketWithTotalResponse}
        graphReportPreviousYear={histogramDataPreviousYear as GraphBucketWithTotalResponse}
        granularity={props.filters.granularity}
      />
    )
  } else if (comparing) {
    if (previousYear && compareData && compareDataPreviousYear) {
      exportData = compareDataPreviousYear.data.map((element, index) => [element, compareData.data[index]]).flat()
    } else if (!previousYear && compareData) {
      exportData = compareData
    } else {
      exportData = []
    }
    chart = compareData && (
      <NumberOfTripsPerProviderPerDay
        title={title}
        graphReport={compareData as GraphCompareWithTotalResponse}
        graphReportPreviousYear={previousYear ? (compareDataPreviousYear as GraphCompareWithTotalResponse) : undefined}
        granularity={props.filters.granularity}
      />
    )
  }

  return (
    <ChartWrapper
      id={props.id}
      title={title}
      description={description}
      formatExportBuckets={formatExportBuckets}
      toggles={toggles}
      loading={loading}
      data={exportData}
    >
      {chart}
    </ChartWrapper>
  )
}

function TripsPerZoneComponent(props: IGraphComponent) {
  const { t } = useTranslation()

  const [origin, setDisplayOrigin] = useState(true)
  const layers = useMapLayer()
  const title = origin
    ? upperFirst(t('kpi.TripsPerOriginZone.title'))
    : upperFirst(t('kpi.TripsPerDestinationZone.title'))
  const description = origin
    ? upperFirst(t('kpi.TripsPerOriginZone.description'))
    : upperFirst(t('kpi.TripsPerDestinationZone.description'))

  async function fetcher(): Promise<FeatureCollection> {
    const data = createPayloadFromFilters(props.filters)
    data.zone = origin ? GraphFilters_ZoneEnum.origin : GraphFilters_ZoneEnum.destination
    const layer = layers.find((layers) => layers.id === data.layer)

    if (layer) {
      const layerFeatures = getLayerFeatures(layer)
      data.limit = layerFeatures.length || 20
      return api.graphs.graphNumberOfTripsPerZone({ data }).then((results: GraphData) => {
        return graphDataToFeatureCollection(results, layerFeatures)
      })
    } else {
      return Promise.resolve({ type: FeatureCollection_TypeEnum.FeatureCollection, features: [] })
    }
  }

  function render(data: IFeatureCollection, title?: string) {
    return <TripsPerZone title={title} data={data} origin={origin} />
  }

  let formatExportFeatures
  if (userHasPermission(['analyze', 'kpi'])) {
    formatExportFeatures = origin ? formatExportTripsPerZoneOrigin() : formatExportTripsPerZoneDestination()
  }

  return (
    <DashboardGraphWrapper
      title={title}
      fetcher={fetcher}
      render={render}
      // exporter={exportTripsPerZone(origin)}
      formatExportFeatures={formatExportFeatures}
      description={description}
      toggles={[
        {
          active: origin,
          onToggle: () => setDisplayOrigin(!origin),
          activeIcon: 'toggle-off',
          inactiveIcon: 'toggle-on',
          activeText: t('kpi.export.Toggle.activeText'),
          inactiveText: t('kpi.export.Toggle.inactiveText'),
        },
      ]}
      // actually, updateOn does not work with multi variables
      // if this component re-renders with the same layers and origin,
      // the object passed is not the same, so it will retrigger a fetch
      updateOn={[layers, origin, props.filters]}
    />
  )
}

function TripsPerProviderComponent(props: IGraphComponent) {
  const { t } = useTranslation()

  const [loading, setLoading] = useState(true)
  const [previousYear, setPreviousYear] = useState<boolean>(false)
  const [graphData, setGraphData] = useState<GraphStackResponse[]>([])
  const [graphDataPreviousYear, setGraphDataPreviousYear] = useState<GraphStackResponse[] | undefined>(undefined)

  let description = upperFirst(t('kpi.TripsPerProvider.description'))
  if (previousYear) {
    description = `${description}. ${t('kpi.TripsPerProvider.previousYearDescription')}`
  }

  const fetchData = useDebouncedLoadDataCallback(
    (payload: GraphFilters) => api.graphs.graphPercentageOfTripsPerProvider({ data: payload }),
    props.filters,
    setLoading,
    setGraphData
  )

  const fetchDataPreviousYear = useDebouncedLoadDataCallback(
    (payload: GraphFilters) => api.graphs.graphPercentageOfTripsPerProvider({ data: payload }),
    props.filters,
    setLoading,
    setGraphDataPreviousYear,
    true
  )

  useEffect(() => {
    if (previousYear) {
      fetchDataPreviousYear()
    } else {
      fetchData()
    }
  }, [previousYear, props.filters])

  let formatExportBuckets
  if (userHasPermission(['analyze', 'kpi'])) {
    formatExportBuckets = formatExportTripsPerProvider(props.filters.granularity)
  }

  const toggles = []
  if ([GranularityEnum.quarter, GranularityEnum.month].includes(props.filters.granularity)) {
    toggles.push({
      active: previousYear,
      onToggle: () => setPreviousYear(!previousYear),
      activeIcon: 'review-off',
      inactiveIcon: 'review-on',
      activeText: t('kpi.TripsCharts.togglePreviousYearActiveText'),
      inactiveText: t('kpi.TripsCharts.togglePreviousYearInactiveText'),
    })
  }

  let chart: React.ReactNode
  let exportData: any
  if (previousYear) {
    if (graphData && graphDataPreviousYear) {
      exportData = graphDataPreviousYear.map((element, index) => [element, graphData[index]]).flat()
    }
    chart = graphDataPreviousYear && (
      <PercentageofTripsPerProviderWithPreviousYear
        title={props.title}
        graphReport={graphData}
        graphReportPreviousYear={graphDataPreviousYear}
        granularity={props.filters.granularity}
      />
    )
  } else {
    exportData = graphData
    chart = <PercentageofTripsPerProvider graphReport={graphData} granularity={props.filters.granularity} />
  }

  return (
    <ChartWrapper
      id={props.id}
      title={upperFirst(t('kpi.TripsPerProvider.title'))}
      description={description}
      formatExportBuckets={formatExportBuckets}
      toggles={toggles}
      loading={loading}
      data={exportData}
    >
      {chart}
    </ChartWrapper>
  )
}

export function OriginDestinationSankeyComponent(props: IGraphComponent) {
  const { t } = useTranslation()

  const layers = useMapLayer()
  const [showInnerTraffic, toggleInnerTraffic] = useState<boolean>(true)

  const filters = props.filters
  filters.limit = MAX_DISPLAYED_AREAS

  async function fetcher(): Promise<GraphFlowsResponse> {
    return await api.graphs.graphTripFlows({ data: createPayloadFromFilters(filters) })
  }

  function render(response: GraphFlowsResponse, title?: string) {
    return <OriginDestinationSankey title={title} data={response} showInnerTraffic={showInnerTraffic} />
  }

  function hideBanner(data: GraphFlowsResponse, bannerMessage?: string): string {
    const flowsLength = (data.flows && Object.keys(data.flows).length) || 0
    if (flowsLength === 0) {
      return t('kpi.OriginDestinationSankey.noTrips') // if there is 0 trips
    }
    if (bannerMessage && flowsLength >= MAX_DISPLAYED_AREAS) {
      return bannerMessage // the limit has been exceeded
    }
    return ''
  }

  let customExport
  if (userHasPermission(['analyze', 'kpi'])) {
    customExport = exportOriginDestinationSankey(filters, showInnerTraffic)
  }

  return (
    <DashboardGraphWrapper
      title={upperFirst(t('kpi.OriginDestinationSankey.title'))}
      description={upperFirst(t('kpi.OriginDestinationSankey.description'))}
      fetcher={fetcher}
      render={render}
      hideBanner={hideBanner}
      disableImageExport
      toggles={[
        {
          active: showInnerTraffic,
          onToggle: () => toggleInnerTraffic(!showInnerTraffic),
          activeIcon: 'toggle-off',
          inactiveIcon: 'toggle-on',
          activeText: t('kpi.OriginDestinationSankey.doNotShowInnerTraffic'),
          inactiveText: t('kpi.OriginDestinationSankey.showInnerTraffic'),
        },
      ]}
      updateOn={[layers, showInnerTraffic, props.filters]}
      customExport={customExport}
      bannerMessage={t('kpi.OriginDestinationSankey.warningAreaLimit', {
        limit: MAX_DISPLAYED_AREAS,
      })}
    />
  )
}

function TotalDistanceComponent(props: IGraphComponent) {
  const { t } = useTranslation()
  const { distance_unit } = useAgencySettings()
  const isProviderUser = useProviderUser()
  const [comparing, setComparing] = useState<boolean>(false)
  const [previousYear, setPreviousYear] = useState<boolean>(false)
  const [loading, setLoading] = useState(true)
  const [compareData, setCompareData] = useState<GraphCompareWithTotalResponse | undefined>(undefined)
  const [compareDataPreviousYear, setCompareDataPreviousYear] = useState<GraphCompareWithTotalResponse | undefined>(
    undefined
  )
  const [histogramData, setHistogramData] = useState<GraphBucketWithTotalResponse | undefined>(undefined)
  const [histogramDataPreviousYear, setHistogramDataPreviousYear] = useState<GraphBucketWithTotalResponse | undefined>(
    undefined
  )

  const distanceUnit = { distance_unit: distance_unit === 'miles' ? 'Miles' : 'Kilometers' }
  const title = comparing
    ? upperFirst(t('kpi.TotalDistance.title.comparing', distanceUnit))
    : upperFirst(t('kpi.TotalDistance.title', distanceUnit))
  let description = comparing
    ? upperFirst(t('kpi.TotalDistance.description.comparing', distanceUnit))
    : upperFirst(t('kpi.TotalDistance.description', distanceUnit))
  if (previousYear) {
    const extraDescription = comparing
      ? t('kpi.TotalDistance.previousYeardescription.comparing')
      : t('kpi.TotalDistance.previousYeardescription')
    description = `${description}. ${extraDescription}`
  }

  const toggles =
    userHasPermission(['read', 'trip']) && !isProviderUser
      ? [
          {
            active: comparing,
            onToggle: () => setComparing(!comparing),
            activeIcon: 'bar',
            inactiveIcon: 'line',
            activeText: t('kpi.TripsCharts.toggleIconActiveText'),
            inactiveText: t('kpi.TripsCharts.toggleIconInactiveText'),
          },
        ]
      : []
  if ([GranularityEnum.quarter, GranularityEnum.month].includes(props.filters.granularity)) {
    toggles.push({
      active: previousYear,
      onToggle: () => setPreviousYear(!previousYear),
      activeIcon: 'review-off',
      inactiveIcon: 'review-on',
      activeText: t('kpi.TripsCharts.togglePreviousYearActiveText'),
      inactiveText: t('kpi.TripsCharts.togglePreviousYearInactiveText'),
    })
  }

  const fetchCompareData = useDebouncedLoadDataCallback(
    (payload: GraphFilters) => api.graphs.graphTotalDistancePerProvider({ data: payload }),
    props.filters,
    setLoading,
    setCompareData
  )
  const fetchCompareDataPreviousYear = useDebouncedLoadDataCallback(
    (payload: GraphFilters) => api.graphs.graphTotalDistancePerProvider({ data: payload }),
    props.filters,
    setLoading,
    setCompareDataPreviousYear,
    true
  )
  const fetchHistogramData = useDebouncedLoadDataCallback(
    (payload: GraphFilters) => api.graphs.graphTotalDistance({ data: payload }),
    props.filters,
    setLoading,
    setHistogramData
  )
  const fetchHistogramDataPreviousYear = useDebouncedLoadDataCallback(
    (payload: GraphFilters) => api.graphs.graphTotalDistance({ data: payload }),
    props.filters,
    setLoading,
    setHistogramDataPreviousYear,
    true
  )

  useEffect(() => {
    if (comparing) {
      fetchCompareData()
      if (previousYear) {
        fetchCompareDataPreviousYear()
      }
    } else {
      fetchHistogramData()
      if (previousYear) {
        fetchHistogramDataPreviousYear()
      }
    }
  }, [comparing, previousYear, props.filters])

  let formatExportBuckets
  if (userHasPermission(['analyze', 'kpi'])) {
    formatExportBuckets = comparing
      ? formatExportComparing(props.filters.granularity)
      : formatExportTotal(props.filters.granularity, title)
  }

  let chart: React.ReactNode
  let exportData: any
  if (!comparing && !previousYear) {
    exportData = histogramData
    chart = histogramData && (
      <TotalDistance
        title={title}
        graphReport={histogramData as GraphBucketWithTotalResponse}
        granularity={props.filters.granularity}
      />
    )
  } else if (!comparing && previousYear) {
    if (histogramData && histogramDataPreviousYear) {
      exportData = {
        data: histogramDataPreviousYear.data.map((element, index) => [element, histogramData.data[index]]).flat(),
      }
    }
    chart = histogramData && histogramDataPreviousYear && (
      <TotalDistanceWithPreviousYear
        title={title}
        graphReport={histogramData as GraphBucketWithTotalResponse}
        graphReportPreviousYear={histogramDataPreviousYear as GraphBucketWithTotalResponse}
        granularity={props.filters.granularity}
      />
    )
  } else if (comparing) {
    if (previousYear && compareData && compareDataPreviousYear) {
      exportData = compareDataPreviousYear.data.map((element, index) => [element, compareData.data[index]]).flat()
    } else if (!previousYear && compareData) {
      exportData = compareData
    } else {
      exportData = []
    }
    chart = compareData && (
      <TotalDistancePerProvider
        title={title}
        graphReport={compareData as GraphCompareWithTotalResponse}
        graphReportPreviousYear={previousYear ? (compareDataPreviousYear as GraphCompareWithTotalResponse) : undefined}
        granularity={props.filters.granularity}
      />
    )
  }

  return (
    <ChartWrapper
      title={title}
      description={description}
      formatExportBuckets={formatExportBuckets}
      toggles={toggles}
      loading={loading}
      data={exportData}
    >
      {chart}
    </ChartWrapper>
  )
}

function TripsDistributionComponent(props: IGraphComponent) {
  const { t } = useTranslation()
  const [comparing, setComparing] = useState<boolean>(false)
  const [loading, setLoading] = useState(true)
  const [graphData, setGraphData] = useState<GraphStackWithAverageResponse | undefined>(undefined)

  const fetchData = useDebouncedLoadDataCallback(
    (payload: GraphFilters) => api.graphs.graphTripsDistribution({ data: payload }),
    props.filters,
    setLoading,
    setGraphData
  )

  useEffect(() => {
    fetchData()
  }, [props.filters])

  const title = comparing
    ? upperFirst(t('kpi.TripsDistribution.title.comparing'))
    : upperFirst(t('kpi.TripsDistribution.title'))
  const description = comparing
    ? upperFirst(t('kpi.TripsDistribution.description.comparing'))
    : upperFirst(t('kpi.TripsDistribution.description'))

  let customExport
  if (userHasPermission(['analyze', 'kpi'])) {
    customExport = comparing ? exportTripsDistributionComparing() : exportTripsDistribution()
  }

  return (
    <ChartWrapper
      title={title}
      description={description}
      customExport={customExport}
      toggles={[
        {
          active: comparing,
          activeIcon: 'bar',
          activeText: t('Aggregate view'),
          inactiveIcon: 'line',
          inactiveText: t('Comparison view'),
          onToggle: () => setComparing(!comparing),
        },
      ]}
      loading={loading}
      data={graphData}
    >
      {comparing
        ? graphData && (
            <TripsDistributionComparing
              title={title}
              graphReport={graphData as GraphStackWithAverageResponse}
              granularity={props.filters.granularity}
            />
          )
        : graphData && (
            <TripsDistribution
              title={title}
              graphReport={graphData as GraphStackWithAverageResponse}
              granularity={props.filters.granularity}
            />
          )}
    </ChartWrapper>
  )
}

function TripsDurationComponent(props: IGraphComponent) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [graphData, setGraphData] = useState<GraphStackWithAverageResponse | undefined>(undefined)

  const fetchData = useDebouncedLoadDataCallback(
    (payload: GraphFilters) => api.graphs.graphTripsDuration({ data: payload }),
    props.filters,
    setLoading,
    setGraphData
  )

  useEffect(() => {
    fetchData()
  }, [props.filters])

  let formatExportBuckets
  if (userHasPermission(['analyze', 'kpi'])) {
    formatExportBuckets = exportTripsDuration()
  }

  return (
    <ChartWrapper
      id={props.id}
      title={upperFirst(t('kpi.TripsDuration.title'))}
      description={upperFirst(t('kpi.TripsDuration.description'))}
      formatExportBuckets={formatExportBuckets}
      loading={loading}
      data={graphData}
    >
      {graphData && (
        <TripsDuration
          title={props.title}
          graphReport={graphData as GraphStackWithAverageResponse}
          granularity={props.filters.granularity}
        />
      )}
    </ChartWrapper>
  )
}

function TripsCloseToPublicTransportsComponent(props: IGraphComponent) {
  const { t } = useTranslation()

  const [zone, setZone] = useState<GraphFilters_ZoneEnum>(GraphFilters_ZoneEnum.origin)

  let title: string
  if (zone === GraphFilters_ZoneEnum.origin) {
    title = upperFirst(t('kpi.TripsCloseToPublicTransportsOrigin.title'))
  } else {
    title = upperFirst(t('kpi.TripsCloseToPublicTransportsDestination.title'))
  }

  async function fetcher(): Promise<GraphCompareResponse[]> {
    const data = createPayloadFromFilters(props.filters)
    return await api.graphs.graphPercentageOfTripsCloseToPublicTransports({ data: { ...data, zone: zone } })
  }

  function render(data: GraphCompareResponse[], _?: string) {
    const final_data: GraphCompareResponse[] = data.map((graphCompareResponse) => ({
      ...graphCompareResponse,
      compareBy: {
        buckets: graphCompareResponse.compareBy.buckets.map((bucket) => ({
          ...bucket,
          key: t(enumsTranslationKeys.LineType[bucket.key.split('.')[0]]),
        })),
      },
    }))

    return (
      <TripsCloseToPublicTransports
        title={title}
        graphReport={final_data}
        granularity={props.filters.granularity}
        enableAnimation={false}
      />
    )
  }

  let formatExportBuckets
  if (userHasPermission([DomainPermission_VerbEnum.analyze, DomainPermission_DomainEnum.trip])) {
    formatExportBuckets = formatExportClosePublicTransportStops(props.filters.granularity)
  }

  return (
    <DashboardGraphWrapper
      updateOn={[zone, props.filters]}
      title={title}
      fetcher={fetcher}
      render={render}
      formatExportBuckets={formatExportBuckets}
      toggles={[
        {
          active: zone === GraphFilters_ZoneEnum.origin,
          activeIcon: 'toggle-off',
          inactiveIcon: 'toggle-on',
          activeText: t('Show Destination Data'),
          inactiveText: t('Show Origin Data'),
          onToggle: () =>
            setZone(
              zone === GraphFilters_ZoneEnum.origin ? GraphFilters_ZoneEnum.destination : GraphFilters_ZoneEnum.origin
            ),
        },
      ]}
    />
  )
}

interface ITripsChartsProps {
  filters: IFilters
}

function TripsCharts(props: ITripsChartsProps) {
  const isProviderUser = useProviderUser()

  useEffect(scrollToComponent)

  return (
    <BaseChartWrapper>
      <WarnBanner toDate={props.filters.range.to} />
      <Can I="summarize" an="kpi">
        <TripsPerDayComponent id="trips-per-day" filters={props.filters} />
      </Can>
      {isProviderUser ? null : (
        <Can I="read" an="trip">
          <TripsPerProviderComponent filters={props.filters} />
        </Can>
      )}
      <Can I={DomainPermission_VerbEnum.analyze} an={DomainPermission_DomainEnum.trip}>
        <TripsCloseToPublicTransportsComponent filters={props.filters} />
      </Can>
      <Can I="summarize" an="kpi">
        <TotalDistanceComponent filters={props.filters} />
        {isProviderUser ? null : <TripsDistributionComponent filters={props.filters} />}
        <TripsDurationComponent filters={props.filters} />
      </Can>
      <Can I="read" an="trip">
        {isProviderUser ? null : <TripsPerZoneComponent filters={props.filters} />}
        <OriginDestinationSankeyComponent filters={props.filters} />
      </Can>
    </BaseChartWrapper>
  )
}

export default TripsCharts
